"use client"

import * as React from "react"

import { useQuery, keepPreviousData } from "@tanstack/react-query"
import { Input } from "@workspace/ui/components/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@workspace/ui/components/dialog"
import { Loader } from "@workspace/ui/components/loader"
import { cn } from "@workspace/ui/lib/utils"
import { SearchIcon, UtensilsCrossedIcon } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"

import { useRouter } from "@/i18n/navigation"
import {
  MIN_QUERY_LENGTH,
  type MenuSearchResponse,
  type MenuSearchResult,
} from "@/lib/menu-search"

/**
 * Header menu search with type-ahead suggestions.
 *
 * Shape: a real `<form method="get" action="/{locale}/menu">` wrapping a real
 * text input. With JS off (or before hydration) it still works — submitting
 * lands on `/menu?q=…`, which filters server-side with the SAME matcher the
 * suggestion endpoint uses (`@/lib/menu-search`). The dropdown is an
 * enhancement layered on top, never the only way to search.
 *
 * Fetching: react-query against `/{locale}/api/menu-search` (the provider tree
 * already supplies a `QueryClient`), keyed by the DEBOUNCED query. That buys
 * three things for free that hand-rolled fetching gets wrong — in-flight
 * requests are aborted via the query `signal`, out-of-order responses can't
 * clobber a newer one, and re-typing a query already seen this session renders
 * from cache with no request at all.
 *
 * A11y: an ARIA 1.2 combobox — `role="combobox"` input owning a `role="listbox"`
 * popup, active option tracked with `aria-activedescendant` (focus never leaves
 * the input, so typing is uninterrupted), `aria-live` status for result counts.
 * Not cmdk: the desktop variant has to render as the header's pill-shaped bar,
 * and `CommandInput` hard-codes its own input chrome.
 *
 * Labels come from `useTranslations` here rather than from props (the prop
 * convention `ItemCard` follows): two of them are ICU-pluralized/parameterized
 * at RUNTIME (`searchResultCount`, `searchSeeAll`), and a formatter function
 * cannot cross the server→client boundary at all. `NextIntlClientProvider` is
 * already in the tree (`components/providers.tsx`), so reading them here costs
 * nothing.
 */

/** Resolved label bundle — built client-side so runtime ICU args work. */
interface MenuSearchCopy {
  /** Accessible name of the search field. */
  label: string
  placeholder: string
  /** Accessible name of the suggestion listbox. */
  suggestionsLabel: string
  loading: string
  noResults: string
  /** Row that runs the full search: takes the typed query. */
  seeAll: (query: string) => string
  /** Screen-reader status: how many suggestions are showing. */
  resultCount: (count: number) => string
  soldOut: string
  /** Accessible name of the mobile trigger button. */
  openLabel: string
}

function useMenuSearchCopy(): MenuSearchCopy {
  const t = useTranslations("nav")
  return {
    label: t("searchLabel"),
    placeholder: t("searchPlaceholder"),
    suggestionsLabel: t("searchSuggestionsLabel"),
    loading: t("searchLoading"),
    noResults: t("searchNoResults"),
    seeAll: (query: string) => t("searchSeeAll", { query }),
    resultCount: (count: number) => t("searchResultCount", { count }),
    soldOut: t("searchSoldOut"),
    openLabel: t("searchOpenLabel"),
  }
}

/** How long typing must settle before a request goes out. */
const DEBOUNCE_MS = 200

/** Shared empty result set — see `useSuggestions`. */
const EMPTY_RESULTS: MenuSearchResult[] = []

export function MenuSearch({
  variant = "inline",
  className,
}: {
  /** `inline` = header pill + anchored dropdown; `dialog` = icon button + modal (mobile). */
  variant?: "inline" | "dialog"
  className?: string
}) {
  if (variant === "dialog") {
    return <DialogSearch className={className} />
  }
  return <InlineSearch className={className} />
}

/* -------------------------------------------------------------------------- */
/* Suggestion data                                                            */
/* -------------------------------------------------------------------------- */

/** Debounce a fast-changing value (the raw input) into a request-worthy one. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

function useSuggestions(query: string, locale: string) {
  const debounced = useDebounced(query.trim(), DEBOUNCE_MS)
  const enabled = debounced.length >= MIN_QUERY_LENGTH

  const { data, isFetching } = useQuery({
    queryKey: ["menu-search", locale, debounced],
    enabled,
    // Keep the previous matches on screen while the next ones load, so the list
    // doesn't blank out between keystrokes.
    placeholderData: keepPreviousData,
    // The menu is long-cached server-side; re-asking for a query typed a moment
    // ago buys nothing.
    staleTime: 60_000,
    retry: false,
    queryFn: async ({ signal }): Promise<MenuSearchResult[]> => {
      const response = await fetch(
        `/${locale}/api/menu-search?q=${encodeURIComponent(debounced)}`,
        { signal, headers: { accept: "application/json" } }
      )
      if (!response.ok) throw new Error(`menu-search ${response.status}`)
      const body = (await response.json()) as MenuSearchResponse
      return body.results
    },
  })

  // Stable identity — the active-option reset keys off this array, and a fresh
  // `[]` on every render would re-run that effect forever.
  const results = React.useMemo(
    () => (enabled ? (data ?? EMPTY_RESULTS) : EMPTY_RESULTS),
    [enabled, data]
  )

  return {
    results,
    // Only a first load counts as "loading" — a background refetch keeps the
    // previous list visible instead of flashing a spinner on every keystroke.
    loading: enabled && isFetching && data === undefined,
    enabled,
  }
}

/* -------------------------------------------------------------------------- */
/* Shared keyboard / navigation behavior                                       */
/* -------------------------------------------------------------------------- */

/**
 * Combobox state shared by both variants: query, active option index, and the
 * two ways a search ends (open an item / run the full search).
 */
function useComboboxSearch({
  onNavigate,
}: {
  /** Called right before navigating — lets the dialog variant close itself. */
  onNavigate?: () => void
} = {}) {
  const locale = useLocale()
  const router = useRouter()
  const [query, setQuery] = React.useState("")
  // The highlight is tracked by item id, not index: when the results change
  // under the diner's fingers, an id either still exists (highlight follows the
  // item to its new row) or it doesn't (highlight clears) — no effect needed to
  // resync, and no stale index pointing at a different dish.
  const [activeId, setActiveId] = React.useState<string | null>(null)
  const { results, loading, enabled } = useSuggestions(query, locale)

  const activeIndex = activeId
    ? results.findIndex((result) => result.id === activeId)
    : -1

  const setActiveIndex = React.useCallback(
    (index: number) => setActiveId(results[index]?.id ?? null),
    [results]
  )

  const openItem = React.useCallback(
    (href: string) => {
      onNavigate?.()
      router.push(href)
    },
    [onNavigate, router]
  )

  const runFullSearch = React.useCallback(() => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) return
    onNavigate?.()
    // Object form, not a `/menu?q=…` string: next-intl's locale-aware router
    // treats a string href as a pathname and drops the query string.
    router.push({ pathname: "/menu", query: { q: trimmed } })
  }, [onNavigate, query, router])

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (results.length === 0) return
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((activeIndex + 1) % results.length)
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex(activeIndex <= 0 ? results.length - 1 : activeIndex - 1)
      }
    },
    [activeIndex, results.length, setActiveIndex]
  )

  /** Enter: open the highlighted suggestion, else fall through to full search. */
  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const active = results[activeIndex]
      if (active) {
        openItem(active.href)
        return
      }
      runFullSearch()
    },
    [activeIndex, openItem, results, runFullSearch]
  )

  return {
    locale,
    query,
    setQuery,
    activeIndex,
    setActiveIndex,
    results,
    loading,
    enabled,
    openItem,
    runFullSearch,
    onKeyDown,
    onSubmit,
  }
}

/* -------------------------------------------------------------------------- */
/* Variants                                                                    */
/* -------------------------------------------------------------------------- */

function InlineSearch({ className }: { className?: string }) {
  const listboxId = React.useId()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [focused, setFocused] = React.useState(false)
  const copy = useMenuSearchCopy()
  // Navigating away dismisses the popup — otherwise it stays open behind the
  // item modal and is still there when the diner closes it.
  const search = useComboboxSearch({ onNavigate: () => setFocused(false) })

  const open = focused && search.enabled

  // Clicking anywhere outside dismisses the popup (a blur handler alone would
  // fire before a suggestion's own click could land).
  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setFocused(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  return (
    <div
      ref={containerRef}
      className={cn("relative hidden flex-1 md:block", className)}
    >
      <form
        action={`/${search.locale}/menu`}
        method="get"
        onSubmit={search.onSubmit}
        role="search"
      >
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            name="q"
            value={search.query}
            onChange={(event) => search.setQuery(event.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setFocused(false)
              else {
                setFocused(true)
                search.onKeyDown(event)
              }
            }}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && search.activeIndex >= 0
                ? optionId(listboxId, search.activeIndex)
                : undefined
            }
            aria-label={copy.label}
            placeholder={copy.placeholder}
            className="h-11 rounded-4xl border-transparent bg-muted/50 ps-9"
          />
        </div>
      </form>

      {open ? (
        <div className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg">
          <SuggestionList
            listboxId={listboxId}
            copy={copy}
            results={search.results}
            loading={search.loading}
            query={search.query}
            activeIndex={search.activeIndex}
            onHover={search.setActiveIndex}
            onSelect={search.openItem}
            onSeeAll={search.runFullSearch}
          />
        </div>
      ) : null}
    </div>
  )
}

function DialogSearch({ className }: { className?: string }) {
  const listboxId = React.useId()
  const [open, setOpen] = React.useState(false)
  const copy = useMenuSearchCopy()
  const search = useComboboxSearch({ onNavigate: () => setOpen(false) })

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={copy.openLabel}
        className={cn(
          "inline-flex items-center justify-center rounded-4xl p-2 text-foreground hover:bg-muted md:hidden",
          className
        )}
      >
        <SearchIcon className="size-5" aria-hidden />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        {/* No dialog close button: it would land on top of the search input's
            own clear (✕). Tap-outside and Escape still close, matching how
            `CommandDialog` is configured in packages/ui. */}
        <DialogContent
          className="top-16 max-w-[calc(100%-2rem)] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-lg"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">{copy.label}</DialogTitle>
          <DialogDescription className="sr-only">
            {copy.suggestionsLabel}
          </DialogDescription>

          <form
            action={`/${search.locale}/menu`}
            method="get"
            onSubmit={search.onSubmit}
            role="search"
            className="border-b p-2"
          >
            <div className="relative">
              <SearchIcon
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                name="q"
                autoFocus
                value={search.query}
                onChange={(event) => search.setQuery(event.target.value)}
                onKeyDown={search.onKeyDown}
                role="combobox"
                aria-expanded={search.enabled}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  search.activeIndex >= 0
                    ? optionId(listboxId, search.activeIndex)
                    : undefined
                }
                aria-label={copy.label}
                placeholder={copy.placeholder}
                className="h-11 rounded-4xl border-transparent bg-muted/50 ps-9"
              />
            </div>
          </form>

          {search.enabled ? (
            <SuggestionList
              listboxId={listboxId}
              copy={copy}
              results={search.results}
              loading={search.loading}
              query={search.query}
              activeIndex={search.activeIndex}
              onHover={search.setActiveIndex}
              onSelect={search.openItem}
              onSeeAll={search.runFullSearch}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Suggestion list                                                             */
/* -------------------------------------------------------------------------- */

function optionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`
}

function SuggestionList({
  listboxId,
  copy,
  results,
  loading,
  query,
  activeIndex,
  onHover,
  onSelect,
  onSeeAll,
}: {
  listboxId: string
  copy: MenuSearchCopy
  results: MenuSearchResult[]
  loading: boolean
  query: string
  activeIndex: number
  onHover: (index: number) => void
  onSelect: (href: string) => void
  onSeeAll: () => void
}) {
  return (
    <div className="flex max-h-[min(24rem,60svh)] flex-col overflow-y-auto">
      {/* Result count for screen readers — the visual list is the sighted
          equivalent, so it stays out of the accessible name of anything. The
          loading case announces itself via `Loader`'s own status role. */}
      <span aria-live="polite" className="sr-only">
        {loading ? "" : copy.resultCount(results.length)}
      </span>

      {loading ? (
        // A div, not a <p>: `Loader` renders a div, which is invalid (and a
        // hydration error) inside a paragraph.
        <div className="flex items-center justify-center px-4 py-6">
          <Loader size={28} label={copy.loading} />
        </div>
      ) : results.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {copy.noResults}
        </p>
      ) : (
        <ul role="listbox" id={listboxId} aria-label={copy.suggestionsLabel}>
          {results.map((result, index) => (
            <li
              key={result.id}
              id={optionId(listboxId, index)}
              role="option"
              aria-selected={index === activeIndex}
              // Pointer-down, not click: `click` fires after the input's blur
              // has already torn the popup down.
              onPointerDown={(event) => {
                event.preventDefault()
                onSelect(result.href)
              }}
              onMouseEnter={() => onHover(index)}
              className={cn(
                "flex cursor-pointer items-center gap-3 px-3 py-2 text-start",
                index === activeIndex && "bg-muted"
              )}
            >
              {result.imageUrl ? (
                // Plain <img> for the same reason as `ItemCard`: tenant photo
                // hosts are arbitrary CDNs, not in next.config remotePatterns.
                <img
                  src={result.imageUrl}
                  alt={result.imageAlt ?? ""}
                  loading="lazy"
                  className="size-10 shrink-0 rounded-md bg-muted object-cover"
                />
              ) : (
                <span
                  aria-hidden
                  className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted"
                >
                  <UtensilsCrossedIcon className="size-4 text-muted-foreground" />
                </span>
              )}

              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span className="truncate">{result.name}</span>
                  {!result.available ? (
                    <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium tracking-wide text-destructive uppercase">
                      {copy.soldOut}
                    </span>
                  ) : null}
                </span>
                {result.categoryName || result.shortDescription ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {result.shortDescription || result.categoryName}
                  </span>
                ) : null}
              </span>

              <span className="shrink-0 text-sm font-semibold">
                {result.price}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Escape hatch to the full results page — always reachable, including
          when the capped suggestion list hid some matches. */}
      <button
        type="button"
        onPointerDown={(event) => {
          event.preventDefault()
          onSeeAll()
        }}
        className="border-t px-4 py-2.5 text-start text-sm font-medium text-primary hover:bg-muted"
      >
        {copy.seeAll(query.trim())}
      </button>
    </div>
  )
}
